// ---------------------------------------------------------------------------
// My calendar — everything assigned to ME, across every project, on one grid.
// ---------------------------------------------------------------------------
// Modelled on Google Calendar, which solves exactly this problem: many sources,
// one month. Its two good ideas, both taken here —
//
//   1. The overlay. One grid, several calendars on top of each other, each a
//      colour. Ours are PROJECTS, because that is what a person here actually
//      belongs to several of.
//   2. The checklist. Every calendar is a checkbox you can switch off, so a busy
//      month can be narrowed to the one thing you're trying to think about. The
//      choice sticks — nobody wants to re-uncheck six projects every morning.
//
// What we deliberately do NOT copy: Google colours the whole chip. With a dark
// theme and tasks that are also overdue/blocked/done, a fully-tinted chip has no
// room left to say "this is late". So the DOT carries the project and the TEXT
// carries urgency (see Chip in MonthGrid).
//
// There is no ＋ here. A new task needs a project, and this view spans them —
// guessing one would file work somewhere the person didn't choose.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TaskListItem } from '@cg-projects/shared';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { patchTask } from '@/features/tasks/mutations';
import { MonthGrid } from '@/features/calendar/components/MonthGrid';

/** Stable per project so the colour a person learns this week still means the
 *  same thing next week. Values live in palette.css (--cal-dot-N): same twelve
 *  hues in both themes, deepened in light mode to stay >= 3:1 on cream. */
const PALETTE = Array.from({ length: 12 }, (_, i) => `var(--cal-dot-${i})`);
const colorFor = (areaId: number): string => PALETTE[areaId % PALETTE.length] as string;

const HIDDEN_KEY = 'cg.mycalendar.hidden';

function loadHidden(): Set<number> {
  try {
    const raw = JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? '[]') as unknown;
    return new Set(Array.isArray(raw) ? raw.filter((n): n is number => Number.isInteger(n)) : []);
  } catch {
    return new Set(); // a corrupt preference must not hide someone's work
  }
}

export function MyCalendar({ tasks }: { tasks: TaskListItem[] }) {
  const areas = useAreaStore((s) => s.areas);
  const [hidden, setHidden] = useState<Set<number>>(loadHidden);
  const [railOpen, setRailOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(HIDDEN_KEY, JSON.stringify([...hidden])); } catch { /* private mode */ }
  }, [hidden]);

  /** Projects you actually have work in — not every project you can see. A rail
   *  listing 37 projects when your tasks live in 4 is noise, not control. */
  const projects = useMemo(() => {
    const m = new Map<number, { id: number; name: string; count: number }>();
    for (const t of tasks) {
      const cur = m.get(t.areaId);
      if (cur) cur.count += 1;
      else m.set(t.areaId, { id: t.areaId, name: t.areaName ?? `Project ${t.areaId}`, count: 1 });
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [tasks]);

  // A task can be assigned to you in a project you are not a member of (task-level
  // access), so the area may be absent from the sidebar list. Deadline-first is the
  // right default then — film dates only exist on content teams. Kept as the
  // TIE-BREAK only now (see fieldOf below) — it is a guess when the area is missing,
  // and a task that only carries one of the two dates should never depend on it.
  const isFilmProject = useCallback(
    (areaId: number) => areas.find((a) => a.id === areaId)?.isContentTeam === true,
    [areas],
  );

  // Which field this SPECIFIC task is actually filed under. If only one of the two
  // dates is populated, that field wins outright — no guess required. isFilmProject
  // only breaks the tie when BOTH are set. This produces exactly the same result as
  // the old `isFilmProject ? (filmDate ?? deadline) : (deadline ?? filmDate)` for
  // every combination, so nothing about where a chip lands changes here — what
  // changes is that onReschedule below now asks the SAME question dateOf does,
  // instead of re-deriving film-vs-deadline from isFilmProject alone.
  const fieldOf = useCallback(
    (t: TaskListItem): 'filmDate' | 'deadline' => {
      if (t.filmDate == null) return 'deadline';
      if (t.deadline == null) return 'filmDate';
      return isFilmProject(t.areaId) ? 'filmDate' : 'deadline';
    },
    [isFilmProject],
  );

  const dateOf = useCallback(
    (t: TaskListItem) => (fieldOf(t) === 'filmDate' ? t.filmDate : t.deadline),
    [fieldOf],
  );

  // Reschedule is OPTIMISTIC and LOCAL to this view. `tasks` is a prop MyWork owns as
  // plain component state — not the `useTasksStore` that patchTask's own `optimistic`
  // argument writes to (mutations.ts) — so without this override the chip visually
  // snapped back to its old day until a refetch MyWork never triggers after mount.
  // The drop read as a no-op even though the write reached Odoo (sweep #18a). Cleared
  // (rolled back) on a failed write; patchTask already toasts the reason.
  const [overrides, setOverrides] = useState<Map<number, Partial<Pick<TaskListItem, 'filmDate' | 'deadline'>>>>(
    () => new Map(),
  );

  const onReschedule = useCallback(
    (t: TaskListItem, dateKey: string) => {
      // Write the SAME field the chip is actually filed under (fieldOf), not a fresh
      // isFilmProject check. The two used to disagree whenever only one of the two
      // dates was set: a film-classified task with no filmDate yet was DISPLAYED via
      // its deadline (the fallback), but the old code still wrote filmDate on drop —
      // the field everyone else reads (deadline) never moved (sweep #18b).
      const field = fieldOf(t);
      const patch = field === 'filmDate' ? { filmDate: dateKey } : { deadline: dateKey };

      setOverrides((prev) => {
        const next = new Map(prev);
        next.set(t.id, patch);
        return next;
      });

      void patchTask(t.id, patch, patch).then((ok) => {
        if (!ok) {
          setOverrides((prev) => {
            const next = new Map(prev);
            next.delete(t.id);
            return next;
          });
        }
      });
    },
    [fieldOf],
  );

  // Merge in-flight/settled reschedules over the raw prop so the grid reflects a drop
  // the instant it happens, regardless of whether (or when) MyWork's own state comes
  // to agree. Skipped entirely once nothing is overridden — the common case.
  const effectiveTasks = useMemo(
    () => (overrides.size === 0 ? tasks : tasks.map((t) => (overrides.has(t.id) ? { ...t, ...overrides.get(t.id) } : t))),
    [tasks, overrides],
  );

  const visible = useMemo(() => effectiveTasks.filter((t) => !hidden.has(t.areaId)), [effectiveTasks, hidden]);
  // "450 of 450 shown" over an empty grid was actively misleading: it counted tasks
  // that PASSED THE FILTER, not tasks that could be placed. Allyson has 400 open and
  // 131 dated, so the honest number is the dated one.
  const dated = useMemo(() => visible.filter((t) => dateOf(t)).length, [visible, dateOf]);
  const colorOf = useCallback((t: TaskListItem) => colorFor(t.areaId), []);
  const metaOf = useCallback((t: TaskListItem) => t.areaName ?? null, []);

  const toggle = (id: number) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const shownCount = projects.length - hidden.size;
  const rail = (
    <div className="space-y-0.5">
      <div className="mb-1 flex items-center justify-between px-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-faint">My projects</span>
        {hidden.size > 0 && (
          <button onClick={() => setHidden(new Set())} className="text-[10px] text-muted hover:text-ink">
            show all
          </button>
        )}
      </div>
      {projects.map((p) => {
        const on = !hidden.has(p.id);
        return (
          <label
            key={p.id}
            className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-hover"
            title={`${p.count} task${p.count === 1 ? '' : 's'}`}
          >
            <input
              type="checkbox"
              checked={on}
              onChange={() => toggle(p.id)}
              className="h-3.5 w-3.5 shrink-0 cursor-pointer accent-current"
              style={{ color: colorFor(p.id) }}
            />
            <span className={`truncate ${on ? 'text-ink' : 'text-faint line-through'}`}>{p.name}</span>
            <span className="ml-auto shrink-0 text-[10px] text-faint">{p.count}</span>
          </label>
        );
      })}
      {!projects.length && <p className="px-1.5 text-xs text-faint">Nothing assigned to you yet.</p>}
    </div>
  );

  return (
    <div className="flex h-full min-h-0">
      {/* Desktop rail. On phones it would eat the grid, so it collapses behind a
          button rather than being cut off — the mistake the view switcher made. */}
      <aside className="hidden w-52 shrink-0 overflow-y-auto border-r border-line p-2 md:block">{rail}</aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 px-4 pt-2 md:hidden">
          <button
            onClick={() => setRailOpen((v) => !v)}
            className="rounded border border-line px-2 py-1 text-xs text-muted hover:bg-hover"
          >
            {railOpen ? 'Hide projects' : `Projects (${shownCount}/${projects.length})`}
          </button>
        </div>
        {railOpen && <div className="border-b border-line p-2 md:hidden">{rail}</div>}

        <MonthGrid
          tasks={visible}
          dateOf={dateOf}
          onReschedule={onReschedule}
          colorOf={colorOf}
          metaOf={metaOf}
          autoJump
          hint={`${visible.length} of ${tasks.length} shown · ${dated} dated · drag to reschedule`}
          // `hidden.size && '…'` returned the NUMBER 0 when nothing was hidden, and
          // `{0 && …}` renders nothing — so the one message that would have explained
          // an empty grid was the thing that could never appear. Explicit comparison.
          empty={
            hidden.size > 0 && projects.length > 0
              ? 'Every project is switched off — tick one on the left.'
              : dated === 0
                ? 'None of your open tasks have a date yet, so there is nothing to place on a calendar.'
                : 'Nothing of yours is dated in this month.'
          }
        />
      </div>
    </div>
  );
}
